import { cn } from "@tokenterminal/core/utils/cn"
import Image from "next/image"
import Link from "next/link"
import MarketingHeading from "@/components/common/MarketingHeading"
import { PageMeta } from "@/components/common/Seo/PageMeta"
import { BlogImage } from "@/components/resources/components/BlogImage"
import { Hero } from "@/components/resources/components/Hero"
import { Pagination } from "@/components/resources/components/Pagination"
import {
  LargeTeaser,
  TeaserGrid,
} from "@/components/resources/components/TeaserGrid"
import type { TeaserArchiveProps } from "@/components/resources/data/archives"
import type { Teaser } from "@/components/resources/data/posts.types"
import { getOGImageRatioWidth } from "@/constants/blog-image"
import { BASE_BLOG_PATH } from "@/constants/urls"
import { NewsletterForm } from "../components/NewsletterForm"
import { formatDate } from "../components/ui/utils"

const renderStickies = (items: Teaser[]) => {
  return (
    <div className="flex flex-col gap-2 mb-4">
      {items.map((teaser) => (
        <LargeTeaser teaser={teaser} key={teaser.id ?? teaser.slug} />
      ))}
    </div>
  )
}

const Published = ({
  publishedAt,
  className,
}: Pick<Teaser, "publishedAt"> & { className?: string }) =>
  publishedAt ? (
    <p
      className={cn(
        "text-xs text-mono tracking-[2px] font-medium mb-2 uppercase text-fg-secondary",
        className
      )}
    >
      <time dateTime={formatDate(new Date(publishedAt))}>
        {formatDate(new Date(publishedAt), true)}
      </time>
    </p>
  ) : null

/** One page of a teaser archive: the /resources hub, a category or the newsletter. */
export const CategoryArchive = ({
  meta,
  title,
  lead,
  featured,
  sticky,
  teasers,
  pagination,
}: TeaserArchiveProps) => {
  return (
    <article className="flex flex-col mb-16">
      <PageMeta meta={meta} />
      <Hero title={title} lead={lead} />
      {featured ? (
        <section className="flex flex-col lg:flex-row gap-12 mb-14 mt-6">
          <Link
            href={`${BASE_BLOG_PATH}${featured.category ? `/${featured.category}` : ""}/${featured.slug}`}
            className="w-full lg:w-2/3"
          >
            <div className="bg-bg-surface rounded-2xl overflow-hidden">
              <div className="bg-cover bg-center">
                {/* Add image or background image based on the teaser */}
                {typeof featured.image === "string" ? (
                  <Image
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-auto aspect-blog-cover rounded-lg"
                    width={800}
                    height={getOGImageRatioWidth(800)}
                    quality={100}
                    sizes="(max-width: 1024px) 100vw, 66vw"
                  />
                ) : (
                  <BlogImage
                    width={800}
                    height={getOGImageRatioWidth(800)}
                    alt={featured.title}
                    className="w-full h-auto aspect-blog-cover rounded-lg"
                    {...{
                      category: featured.category,
                      image: featured.image,
                      releaseNumber: featured.releaseNumber,
                      projectMeta: featured.projectMeta,
                    }}
                  />
                )}
              </div>
            </div>
            <div className="mt-6">
              <MarketingHeading level={3} className="text-4xl mb-2">
                {featured.title}
              </MarketingHeading>
              <p className="text-fg-secondary mb-4 text-pretty">
                {featured.teaserText}
              </p>
              <Published publishedAt={featured.publishedAt} />
            </div>
          </Link>
          <div className="flex flex-col gap-2 flex-1">
            <NewsletterForm />
          </div>
        </section>
      ) : null}
      {featured || sticky.length || teasers.length ? (
        <div>
          {renderStickies(sticky)}
          <TeaserGrid teasers={teasers} />
          <Pagination {...pagination} />
        </div>
      ) : (
        <div className="py-12 text-center">
          <p>No posts in this category.</p>
        </div>
      )}
    </article>
  )
}
